// De schermen die stemmers zien. Puur presentatie, zodat de stempagina en de
// preview bij de tekstinstellingen precies dezelfde opmaak gebruiken.
import React from 'react';
import { ArrowRight, Check, Trophy } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import clsx from 'clsx';
import { formatQuicqImage } from '@/utils.ts';
import Logo from '@/components/Logo';
import { Button } from '@/components/Button';
import { PoweredByAfosto } from '@/components/PoweredByAfosto';
import { fillVoterText, type VoterTexts } from '@/lib/voterTexts';
import type { Vote, VoterDetails, VoterDetailsConfig } from '@/lib/types';

export interface Sponsor {
  name: string | null;
  image_url: string | null;
}

export interface VotingPlayer {
  id: string;
  name: string;
  number: number;
  image_url?: string;
}

/** Donkerblauwe donar.nl-omlijsting rond elk stemscherm */
export function VoterPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="theme-donar min-h-screen">
      <main className="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
        {children}
        <div className="flex flex-col items-center pt-8">
          <PoweredByAfosto className="h-5 text-white/50" />
        </div>
      </main>
    </div>
  );
}

interface PlayerCardProps {
  player: VotingPlayer;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

const PlayerCard = React.memo(function PlayerCard({
  player,
  isSelected,
  onSelect,
}: PlayerCardProps) {
  const imageSrc = React.useMemo(
    () =>
      player.image_url
        ? formatQuicqImage(player.image_url, { w: '160', q: '100' })
        : null,
    [player.image_url],
  );

  const imageSrcSet = React.useMemo(
    () =>
      player.image_url
        ? `${formatQuicqImage(player.image_url, { w: '160', q: '100' })} 1x, ${formatQuicqImage(player.image_url, { w: '320', q: '100' })} 2x`
        : null,
    [player.image_url],
  );

  return (
    <label
      className={clsx(
        'relative flex cursor-pointer overflow-hidden rounded-lg bg-blue-800 transition-colors duration-200 hover:bg-blue-700 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-white',
        isSelected ? 'ring-3 ring-orange-500' : 'ring-2 ring-blue-700',
      )}
    >
      <input
        type="radio"
        name="player"
        value={player.id}
        checked={isSelected}
        onChange={() => onSelect(player.id)}
        className="sr-only"
        required
      />
      <div className="flex items-center justify-center p-4 w-14">
        <div
          className={clsx(
            'w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors',
            isSelected
              ? 'border-orange-500 bg-orange-500'
              : 'border-blue-500',
          )}
        >
          {isSelected && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
        </div>
      </div>
      <div className="flex-grow flex flex-col justify-center py-4 pr-4 uppercase">
        <span className="text-2xl font-extrabold">#{player.number}</span>
        <span className="mt-1 text-lg font-bold">{player.name}</span>
      </div>
      <div className="w-24 h-34 flex-shrink-0">
        {imageSrc ? (
          <img
            src={imageSrc}
            srcSet={imageSrcSet!}
            alt={player.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-blue-700 flex items-center justify-center">
            <span className="text-2xl font-extrabold text-white/60">
              #{player.number}
            </span>
          </div>
        )}
      </div>
    </label>
  );
});

interface SponsorImageProps {
  sponsor: Sponsor | null;
  className?: string;
}

function SponsorImage({ sponsor, className }: SponsorImageProps) {
  if (!sponsor?.image_url) return null;

  // Uploads live in Supabase Storage and are served through Quicq; any other
  // URL (e.g. the original Prismic image) is used as-is.
  const isStorageImage = sponsor.image_url.includes('/storage/v1/object/public/');

  return (
    <div className={clsx('mx-auto w-fit bg-white', className)}>
      <img
        src={isStorageImage ? formatQuicqImage(sponsor.image_url, { w: '200', q: '100' }) : sponsor.image_url}
        srcSet={
          isStorageImage
            ? `${formatQuicqImage(sponsor.image_url, { w: '200', q: '100' })} 1x, ${formatQuicqImage(sponsor.image_url, { w: '400', q: '100' })} 2x`
            : undefined
        }
        alt={sponsor.name ? `Sponsor ${sponsor.name}` : 'Sponsor'}
        className="w-full max-w-[200px] h-auto"
      />
    </div>
  );
}

const VOTER_FIELDS = [
  {
    key: 'email',
    label: 'E-mailadres',
    type: 'email',
    autoComplete: 'email',
    placeholder: 'jouw@email.nl',
  },
  {
    key: 'name',
    label: 'Naam',
    type: 'text',
    autoComplete: 'name',
    placeholder: 'Voor- en achternaam',
  },
  {
    key: 'phone',
    label: 'Telefoonnummer',
    type: 'tel',
    autoComplete: 'tel',
    placeholder: '06 12345678',
  },
] as const;

interface VoterDetailsFieldsProps {
  texts: VoterTexts;
  config: VoterDetailsConfig;
  values: VoterDetails;
  onChange: (field: keyof VoterDetails, value: string) => void;
}

function VoterDetailsFields({ texts, config, values, onChange }: VoterDetailsFieldsProps) {
  const fields = VOTER_FIELDS.filter((field) => config[field.key] !== 'hidden');
  if (fields.length === 0) return null;

  return (
    <div className="mt-8 rounded-lg bg-blue-800 ring-2 ring-blue-700 p-4 sm:p-6 space-y-4">
      <p data-voter-text="voting_details_intro">{texts.voting_details_intro}</p>
      {fields.map((field) => {
        const required = config[field.key] === 'required';
        return (
          <div key={field.key}>
            <label
              htmlFor={`voter-${field.key}`}
              className="block text-sm"
            >
              {field.label}
              {!required && (
                <span className="ml-1 text-white/60">(optioneel)</span>
              )}
            </label>
            <input
              id={`voter-${field.key}`}
              name={field.key}
              type={field.type}
              autoComplete={field.autoComplete}
              placeholder={field.placeholder}
              value={values[field.key] ?? ''}
              onChange={(e) => onChange(field.key, e.target.value)}
              required={required}
              maxLength={field.key === 'email' ? 254 : field.key === 'phone' ? 20 : 100}
              className="mt-2 block w-full border-2 border-gray-200 bg-white px-4 py-3.5 text-base text-black opacity-90 outline-0 transition-all placeholder:text-gray-400 focus:opacity-100 focus:outline-2 focus:outline-blue-300"
            />
          </div>
        );
      })}
    </div>
  );
}

function WinnerBanner({ texts, className }: { texts: VoterTexts; className?: string }) {
  return (
    <div
      role="status"
      className={clsx(
        'max-w-2xl mx-auto rounded-lg bg-orange-500 p-6 text-center text-white',
        className,
      )}
    >
      <Trophy className="mx-auto h-12 w-12" />
      <h2 data-voter-text="winner_title" className="mt-4 text-2xl font-extrabold uppercase">
        {texts.winner_title}
      </h2>
      <p data-voter-text="winner_body" className="mt-3">
        {texts.winner_body}
      </p>
    </div>
  );
}

export function NoGameScreen({ texts }: { texts: VoterTexts }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center py-12">
        <Logo className="mx-auto h-28 w-auto" height={112} />
        <h1
          data-voter-text="no_game_title"
          className="mt-8 text-2xl font-extrabold uppercase md:text-3xl"
        >
          {texts.no_game_title}
        </h1>
        <p data-voter-text="no_game_body" className="mt-4">
          {texts.no_game_body}
        </p>
      </div>
    </div>
  );
}

interface BeforeGameScreenProps {
  texts: VoterTexts;
  title: string;
  gameTime: Date;
  sponsor: Sponsor | null;
}

export function BeforeGameScreen({ texts, title, gameTime, sponsor }: BeforeGameScreenProps) {
  const values = {
    aftellen: formatDistanceToNow(gameTime, { locale: nl }),
    tijd: format(gameTime, 'HH:mm'),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center py-12">
        <Logo className="mx-auto h-28 w-auto" height={112} />
        <h1 className="mt-8 text-2xl font-extrabold uppercase md:text-3xl">
          {title}
        </h1>
        <div className="mt-6 max-w-2xl mx-auto">
          <h3 data-voter-text="before_game_title" className="text-xl uppercase">
            {fillVoterText(texts.before_game_title, values)}
          </h3>
          <p data-voter-text="before_game_body" className="mt-4">
            {fillVoterText(texts.before_game_body, values)}
          </p>
        </div>
        <SponsorImage sponsor={sponsor} className="mt-10" />
      </div>
    </div>
  );
}

interface VotingClosedScreenProps {
  texts: VoterTexts;
  title: string;
  sponsor: Sponsor | null;
  isWinner: boolean;
}

export function VotingClosedScreen({ texts, title, sponsor, isWinner }: VotingClosedScreenProps) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center py-12">
        <Logo className="mx-auto h-28 w-auto" height={112} />
        <h1 className="mt-8 text-2xl font-extrabold uppercase md:text-3xl">
          {title}
        </h1>
        <div className="mt-6 max-w-2xl mx-auto">
          {isWinner && <WinnerBanner texts={texts} className="mb-8" />}
          <h3 data-voter-text="closed_title" className="text-xl uppercase">
            {texts.closed_title}
          </h3>
          {!isWinner && (
            <p data-voter-text="closed_body" className="mt-4">
              {texts.closed_body}
            </p>
          )}
          <SponsorImage sponsor={sponsor} className="mt-10" />
        </div>
      </div>
    </div>
  );
}

interface VotingScreenProps {
  texts: VoterTexts;
  title: string;
  sponsor: Sponsor | null;
  hasVoted: boolean;
  /** VotedCard na het stemmen, anders VoteForm */
  children: React.ReactNode;
}

export function VotingScreen({ texts, title, sponsor, hasVoted, children }: VotingScreenProps) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <Logo className="mx-auto h-28 w-auto" height={112} />
        <h1 className="mt-8 text-2xl font-extrabold uppercase md:text-3xl">
          {title}
        </h1>
        <p
          data-voter-text={hasVoted ? 'voted_intro' : 'voting_title'}
          className="mt-4 mb-8 max-w-2xl mx-auto text-xl uppercase"
        >
          {hasVoted
            ? texts.voted_intro
            : fillVoterText(texts.voting_title, { sponsor: sponsor?.name })}
        </p>
        <SponsorImage sponsor={sponsor} />
      </div>
      {children}
    </div>
  );
}

interface VotedCardProps {
  texts: VoterTexts;
  vote: Vote | null;
  isWinner: boolean;
}

export function VotedCard({ texts, vote, isWinner }: VotedCardProps) {
  return (
    <div className="mt-12 text-center">
      {isWinner && <WinnerBanner texts={texts} className="mb-6" />}
      <div className="rounded-lg bg-blue-800 px-6 py-10 max-w-2xl mx-auto">
        <h2 data-voter-text="voted_title" className="text-2xl font-extrabold uppercase">
          {texts.voted_title}
        </h2>
        {vote && (
          <div className="mt-6 rounded-lg p-4 bg-blue-700">
            <p data-voter-text="voted_choice" className="mb-3">
              {texts.voted_choice}
            </p>
            <div className="flex items-center justify-center space-x-4">
              {vote.player_image_url ? (
                <img
                  src={vote.player_image_url}
                  alt={vote.player_name}
                  className="w-16 h-16 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 shrink-0 rounded-full bg-blue-800 flex items-center justify-center">
                  <span className="text-xl font-extrabold text-white/60">
                    #{vote.player_number}
                  </span>
                </div>
              )}
              <div className="text-left">
                <p className="text-xl font-extrabold uppercase">
                  #{vote.player_number} {vote.player_name}
                </p>
              </div>
            </div>
          </div>
        )}
        {!isWinner && (
          <p data-voter-text="voted_body" className="mt-6">
            {texts.voted_body}
          </p>
        )}
      </div>
    </div>
  );
}

interface VoteFormProps {
  texts: VoterTexts;
  players: VotingPlayer[];
  selectedPlayer: string | null;
  onSelectPlayer: (id: string) => void;
  /** Welke persoonsgegevens gevraagd worden; null als dat uit staat */
  voterDetailsConfig: VoterDetailsConfig | null;
  voterDetails: VoterDetails;
  onVoterDetailChange: (field: keyof VoterDetails, value: string) => void;
  submitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export function VoteForm({
  texts,
  players,
  selectedPlayer,
  onSelectPlayer,
  voterDetailsConfig,
  voterDetails,
  onVoterDetailChange,
  submitting,
  onSubmit,
}: VoteFormProps) {
  return (
    <form onSubmit={onSubmit} className="mt-10 max-w-3xl mx-auto">
      <div className="space-y-4">
        {players.map((player) => (
          <PlayerCard
            key={player.id}
            player={player}
            isSelected={selectedPlayer === player.id}
            onSelect={onSelectPlayer}
          />
        ))}
      </div>

      {voterDetailsConfig && (
        <VoterDetailsFields
          texts={texts}
          config={voterDetailsConfig}
          values={voterDetails}
          onChange={onVoterDetailChange}
        />
      )}

      <div className="mt-10 flex justify-center">
        <Button type="submit" disabled={submitting || !selectedPlayer}>
          {submitting ? (
            'Bezig met verzenden...'
          ) : (
            <>
              <span data-voter-text="voting_submit">{texts.voting_submit}</span>
              <ArrowRight className="h-5 w-5" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
